import * as React from 'react';
import { useHover } from '@react-aria/interactions';
import { PolymorphicRef } from '../../../utils/PolymorphicTypes';
import { ButtonComponent, ButtonProps } from './ButtonTypes';
import { Icon, Typography } from '../../foundation';
import { useButtonTheme } from '../../../themes';
import { ButtonInputValidationError } from './ButtonErrors';

/**
 * The Button component is used to render a button.
 */
export const Button:ButtonComponent = React.forwardRef(
  <C extends React.ElementType = 'button'>({
  as,
  children = undefined,
  colorStyle = 'default',
  disabled = false,
  fluid = false,
  icon = undefined,
  iconPosition = 'left',
  id,
  onClick = undefined,
  size = 'md',
  variant = 'primary',
  ...htmlProps
}: ButtonProps<C>, 
ref?:PolymorphicRef<C>) => {
  
  const isLink = as && ((typeof as === 'string' && as === 'a')
  || (typeof as === 'object')) ? true : false;

  const Component = (disabled && isLink) ? 'div' : (as || 'button');
  let { hoverProps, isHovered } = useHover({});

  const { buttonTheme } = useButtonTheme();
  const buttonIconContainerClass = buttonTheme['button-icon-container'];
 
  const buttonClasses = [
    buttonTheme['bsw-button'],
    buttonTheme[variant],
    buttonTheme[colorStyle],
    buttonTheme[`bsw-button-size--${size}`],
    icon ? buttonTheme['button-icon'] : undefined,
    fluid ? buttonTheme['fluid'] : undefined,
    disabled ? buttonTheme['bsw-button--disabled'] : undefined,
    !disabled && isHovered ? buttonTheme['bsw-button--hover'] : undefined
  ].join(' ');

  /* @start Input validation */
  if (!children) {
    throw new ButtonInputValidationError('`children` prop cannot be empty');
  }
  /* @end Input validation */

  const renderButtonIcon = () => {
    const iconPositionClass = iconPosition === 'right' 
      ? buttonTheme['button-icon-right'] 
      : buttonTheme['button-icon-left'] 

    return (
      <span className={iconPositionClass}><Icon name={icon} disabled={disabled} size={'medium'}/></span>
    );
  };

  // Include disabled for elements that it is a valid attribute
  const _showDisabledAttr=(!as || (as && typeof as === 'string' && (as === 'button' || as === 'input'))) ? disabled : undefined;

  const defaultProps = {
      ...htmlProps,
      ...hoverProps,
      onClick:onClick,
  }

  const componentProps = disabled ? {} : defaultProps;

  return (
    <Component
      {...componentProps}
      className={buttonClasses}
      disabled={_showDisabledAttr}
      id={id}
      ref={ref}
      type={htmlProps?.type || (!as || (typeof as === 'string' && as === 'button') ? 'button' : undefined)}
    >
      <div className={buttonIconContainerClass}>
        {iconPosition === 'left' && icon && renderButtonIcon()}
        <Typography tag="div" type="body-02" bold textAlign="center">
          {children}
        </Typography>
        {iconPosition === 'right' && icon && renderButtonIcon()}
      </div>
    </Component>
  );
});
